import * as XLSX from 'xlsx';
import {
  EpiReportDeliveryRow,
  EpiReportComplianceRow,
  EpiReportLifecycleRow,
  EpiReportSummaryRow,
} from '../data/employees';

const EPI_DELIVERY_REASON_LABEL: Record<string, string> = {
  admissao: 'Admissão',
  reposicao: 'Reposição',
  troca_por_desgaste: 'Troca por desgaste',
  perda_extravio: 'Perda/Extravio',
  mudanca_de_funcao: 'Mudança de função',
  entrega_periodica: 'Entrega periódica',
};

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

function statusLabel(s: string): string {
  const labels: Record<string, string> = {
    ok: 'Em dia',
    pendente: 'Pendente',
    vencido: 'Vencido',
    alerta: 'Alerta',
    'sem-prazo': 'Sem prazo',
  };
  return labels[s] ?? s;
}

export function exportEpiReportToExcel(data: {
  deliveries: EpiReportDeliveryRow[];
  compliance: EpiReportComplianceRow[];
  lifecycle: EpiReportLifecycleRow[];
  summary: EpiReportSummaryRow[];
  filters?: { startDate?: string; endDate?: string };
}) {
  const wb = XLSX.utils.book_new();

  // ── Aba 1: Entregas ───────────────────────────────────────────────────────
  const deliveriesData = [
    [
      'Data',
      'Funcionário',
      'Setor',
      'EPI',
      'CA',
      'Categoria',
      'Quantidade',
      'Motivo',
    ],
    ...data.deliveries.map((d) => [
      formatDate(d.date),
      d.employeeName,
      d.sector ?? '—',
      d.epiName,
      d.ca ?? '—',
      d.category ?? '—',
      d.quantity,
      d.reason ? EPI_DELIVERY_REASON_LABEL[d.reason] ?? d.reason : '—',
    ]),
  ];
  const wsDeliveries = XLSX.utils.aoa_to_sheet(deliveriesData);
  wsDeliveries['!cols'] = [12, 22, 16, 24, 10, 14, 10, 20].map((w) => ({
    wch: w,
  }));
  XLSX.utils.book_append_sheet(wb, wsDeliveries, 'Entregas');

  // ── Aba 2: Conformidade e Pendências ─────────────────────────────────────
  const complianceData = [
    ['Funcionário', 'Setor', 'EPI', 'CA', 'Última Entrega', 'Status'],
    ...data.compliance.map((c) => [
      c.employeeName,
      c.sector ?? '—',
      c.epiName,
      c.ca ?? '—',
      formatDate(c.lastDeliveredAt),
      statusLabel(c.status),
    ]),
  ];
  const wsCompliance = XLSX.utils.aoa_to_sheet(complianceData);
  wsCompliance['!cols'] = [22, 16, 24, 10, 14, 12].map((w) => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, wsCompliance, 'Conformidade');

  // ── Aba 3: Ciclo de Vida ──────────────────────────────────────────────────
  const lifecycleData = [
    [
      'Funcionário',
      'Setor',
      'EPI',
      'Tipo',
      'Última Entrega',
      'Dias desde',
      'Próx. Troca',
      'Status',
    ],
    ...data.lifecycle.map((l) => [
      l.employeeName,
      l.sector ?? '—',
      l.epiName,
      l.controlType === 'descartavel'
        ? 'Descartável'
        : l.controlType === 'por_condicao'
        ? 'Por condição'
        : 'Reutilizável',
      formatDate(l.lastDeliveredAt),
      l.daysSince !== null ? l.daysSince : '—',
      formatDate(l.nextDueDate),
      statusLabel(l.status),
    ]),
  ];
  const wsLifecycle = XLSX.utils.aoa_to_sheet(lifecycleData);
  wsLifecycle['!cols'] = [22, 16, 24, 14, 14, 10, 14, 12].map((w) => ({
    wch: w,
  }));
  XLSX.utils.book_append_sheet(wb, wsLifecycle, 'Ciclo de Vida');

  // ── Aba 4: Consolidado por EPI ────────────────────────────────────────────
  const summaryData = [
    ['EPI', 'CA', 'Categoria', 'Total Entregas', 'Funcionários Atendidos'],
    ...data.summary.map((s) => [
      s.epiName,
      s.ca ?? '—',
      s.category ?? '—',
      s.totalDeliveries,
      s.totalEmployees,
    ]),
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [26, 10, 16, 14, 20].map((w) => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Consolidado por EPI');

  const period =
    data.filters?.startDate && data.filters?.endDate
      ? `${formatDate(data.filters.startDate)}_a_${formatDate(
          data.filters.endDate,
        )}`
      : new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');

  XLSX.writeFile(wb, `relatorio-epi-${period}.xlsx`);
}
